import { inject, watch } from "aurelia";
import { nhost } from '@lib/nhost';
import { IRouter } from '@aurelia/router';
import { IEventAggregator } from 'aurelia';
import { newInstanceForScope } from '@aurelia/kernel';
import { IValidationRules } from '@aurelia/validation';
import { StorageUploadFileResponse } from '@nhost/nhost-js';
import { IValidationController } from '@aurelia/validation-html';
import { IBrandGuidService } from './services/brand-guide-service';
import chroma from 'chroma-js';
import WebFont from 'webfontloader';
import { IConfirmAction } from '@components/confirm-action';
import { IFormInputOption } from "@components/form/interfaces/form-interface";
import { brandGuideSegment, personalityData, segmentImages, socialMediaData, voiceToneData } from "./data";
import { ProgressBarComponent } from "@components/progress-bar/progress-bar-component";
import { IZeusClient, ZeusClient } from "@services/graphql-service/zeus-client";
import { IDialogService } from "@aurelia/dialog";
import { RestartBrandComponent } from "./components/restart-brand/restart-brand-component";
import { brands_constraint, brands_update_column } from "@zeus";
import { PostExampleDrawerComponent } from "./components/post-example-drawer/post-example-drawer-component";
import { uuid4 } from "@helpers/crypto/crypto";
import { Config, driver, Driver, DriveStep, State } from "driver.js";
import { IAuthService } from "@interfaces/auth-service/auth-service-interface";
import tippy from "tippy.js";
import Shepherd from "shepherd.js";

export interface IStep {
  isCompleted: boolean;
  title: string;
  progressBarRef: ProgressBarComponent;
}

@inject(IZeusClient, IAuthService, IRouter, IDialogService, IEventAggregator, IBrandGuidService, IValidationRules, newInstanceForScope(IValidationController), IConfirmAction)
export class BrandGuidePage {

  private brandName: string;
  private brandSegment: IFormInputOption;
  private brandSegmentValue: string;
  private brandDescription: string;
  private brandCharacteristics: string[] = [
  ];
  private newCharacteristic: string

  logoFile: File;
  logoFileId: string;
  private hasntBrandLogo: boolean = false;
  backgroundColor: string;
  foregroundColor: string;
  accentColor: string;

  private brandVoiceTone: IFormInputOption
  private brandVoiceToneValue: string
  private brandVoiceTones: IFormInputOption[] = []
  private brandPersonality: IFormInputOption
  private brandPersonalityValue: string
  private brandPersonalities: IFormInputOption[] = []
  private brandTargetAudience: string
  private brandSocialMedias: string[] = []

  private currentFile: CurrentFile;

  private brandId: string;

  private colorErrors: string;

  private titleRef: HTMLElement;
  private headlineRef: HTMLElement;

  private isEditOrCreateBrand: boolean = false;

  private createBrandProgressBarRef: ProgressBarComponent
  private mainProgressBarValue: number = 0
  private showInputAddCharacteristic: boolean = false;

  private steps: IStep[] = [
    { isCompleted: false, title: 'sobre', progressBarRef: null },
    { isCompleted: false, title: 'visual', progressBarRef: null },
    { isCompleted: false, title: 'estilo', progressBarRef: null }
  ]

  private currentStep: number = 0;

  private socialMedias = socialMediaData

  private postDescription: string;

  private segmentImages = segmentImages

  private finishBrandLoading: boolean = false;

  private brandGuideSegment: IFormInputOption[] = []
  private personalityData: IFormInputOption[] = []
  private voiceToneData: IFormInputOption[] = []

  private brandOnboardCurrentStep: number
  private isBrandOnboardCompleted: boolean

  onboardDriver: Driver;
  timeout: NodeJS.Timeout

  private tour: Shepherd.Tour;
  private initialOnboard: Shepherd.Tour;

  constructor(
    private zeusClient: ZeusClient,
    private auth: IAuthService,
    private router: IRouter,
    private dialogService: IDialogService,
    readonly ea: IEventAggregator,
    private service: IBrandGuidService,
    readonly validationRules: IValidationRules,
    readonly validationController: IValidationController,
    private confirmAction: IConfirmAction,
  ) {
    this.tour = new Shepherd.Tour({
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: {
          enabled: true
        },
        modalOverlayOpeningPadding: 5,
      },
      useModalOverlay: true,
    });

    this.tour.on('show', async (event) => {
      const currentIndex = this.tour.steps.indexOf(event.step);
      const isLastStep = currentIndex === (this.tour.steps.length - 1);

      await this.saveOnboardStep(currentIndex, isLastStep)
    });
  }

  async saveOnboardStep(currentIndex: number, isLastStep: boolean) {
    await this.zeusClient.chain('mutation')({
      update_userOnboarding_by_pk: [
        {
          pk_columns: { userId: this.auth.getUser().id },
          _set: {
            brandOnboardCurrentStep: currentIndex,
            isBrandOnboardCompleted: isLastStep,
          }
        },
        {
          brandOnboardCurrentStep: true,
          isBrandOnboardCompleted: true,
        }
      ]
    })
  }

  async attached() {
    await this.getBrands();
    this.brandGuideSegment = brandGuideSegment
    this.personalityData = personalityData.slice(0, 20)
    this.voiceToneData = voiceToneData.slice(0, 20)

    this.personalityData = personalityData.filter((i) => !this.brandPersonalities.includes(i)).slice(0, 20)

    this.voiceToneData = voiceToneData.filter((i) => !this.brandVoiceTones.includes(i)).slice(0, 20)
  }

  startTour() {
    this.initialOnboard = new Shepherd.Tour({
      defaultStepOptions: {
        scrollTo: false,
        cancelIcon: { enabled: true },
      },
      useModalOverlay: false
    });

    this.initialOnboard.addStep({
      title: 'Pronto para começar?',
      classes: 'copymagico-popover static-popover',
      text: 'Vamos te ajudar a como definir sua marca rápido e com total controle em 3 passos rápidos.',
      buttons: [
        {
          text: '<div class="icon-box-md -mr-2"><i hidden.class="isLoading" class="mdi mdi-arrow-right-circle-outline"></i></div> <span>Começar agora</span>',
          classes: 'shepherd-btn btn-right-icon',
          action: () => {
            this.initialOnboard.complete();
            this.startMainTour();
          }
        }
      ],
    });

    this.initialOnboard.start();
  }

  private startMainTour() {
    this.tour.addStep({
      id: 'first-step',
      classes: `copymagico-popover ${window.innerWidth < 768 ? 'static-popover' : ''}`,
      title: 'Crie, edite e altere.',
      text: 'Nessa área você responde informações que nos ajudam a identificar sua personalidade.',
      attachTo: {
        element: '#brand-guide-form',
        on: 'right-start',
      },
      buttons: [
        {
          text: '<div class="icon-box-md -mr-2"><i hidden.class="isLoading" class="mdi mdi-arrow-right-circle-outline"></i></div> <span>Próximo</span>',
          classes: 'shepherd-btn btn-right-icon',
          action: () => {
            if (window.innerWidth < 768) {
              const element = document.getElementById('open-post-example-drawer')
              if (element) {
                element.click();
                setTimeout(() => {
                  this.tour.next()
                }, 400)
              }
            } else {
              this.tour.next()
            }
          }
        }
      ],
    });

    this.tour.addStep({
      id: 'second-step',
      classes: `copymagico-popover ${window.innerWidth < 768 ? 'static-popover' : ''}`,
      title: 'Veja seu resultado na hora!',
      text: 'A cada etapa, vamos mostrando como sua marca está sendo construída com um post de exemplo.',
      attachTo: {
        element: window.innerWidth < 768 ? '#post-example-mobile' : '#post-example',
        on: 'left-start'
      },
      buttons: [
        {
          text: '<div class="icon-box-md"><i class="mdi mdi-arrow-left-circle-outline"></i></div>',
          classes: 'shepherd-btn btn-left-icon',
          action: () => {

            if (window.innerWidth < 768) {
              const element = document.getElementById('close-post-example-drawer')
              if (element) {
                element.click();
              }
            }

            this.tour.back()
          }
        },
        {
          text: '<div class="icon-box-md -mr-2"><i hidden.class="isLoading" class="mdi mdi-arrow-right-circle-outline"></i></div> <span>Próximo</span>',
          classes: 'shepherd-btn btn-right-icon',
          action: this.tour.next
        }
      ],
    });

    this.tour.addStep({
      id: 'third-step',
      classes: `copymagico-popover ${window.innerWidth < 768 ? 'static-popover' : ''}`,
      title: 'Avance ou retorne.',
      text: 'Acima você vê seu progresso geral, ao lado você vê suas etapas e pode navegar entre elas.',
      attachTo: {
        element: '#progress-bar-example',
        on: 'right'
      },
      buttons: [
        {
          text: '<div class="icon-box-md"><i class="mdi mdi-arrow-left-circle-outline"></i></div>',
          classes: 'shepherd-btn btn-left-icon',
          action: () => {
            if (window.innerWidth < 768) {
              const element = document.getElementById('open-post-example-drawer')
              if (element) {
                element.click();
                setTimeout(() => {
                  this.tour.back()
                }, 400)
              }
            } else {
              this.tour.back()
            }
          }
        },
        {
          text: 'Finalizar',
          classes: 'shepherd-btn btn-right-icon',
          action: this.tour.complete
        }
      ],
      when: {
        show: () => {
          if (window.innerWidth < 768) {
            const element = document.getElementById('close-post-example-drawer')
            if (element) {
              element.click();
            }
          }
        }
      }
    });

    this.tour.start();
  }

  detaching() {
    clearTimeout(this.timeout);

    this.initialOnboard?.cancel();
    this.tour?.cancel();
  }

  private async getBrands() {
    const result = await this.service.getBrands();

    this.brandOnboardCurrentStep = result.userOnboarding_by_pk?.brandOnboardCurrentStep
    this.isBrandOnboardCompleted = result.userOnboarding_by_pk?.isBrandOnboardCompleted

    if (!result.brands.length) return;

    const brand = result.brands[0];

    this.brandId = brand?.id;
    this.brandName = brand?.brandName
    this.brandSegment = brandGuideSegment?.find(item => item.value === brand?.segment);
    this.brandSegmentValue = brand?.segment;
    this.brandDescription = brand?.brandDescription;
    this.brandCharacteristics = brand?.brandCharacteristics?.length ? brand?.brandCharacteristics : []
    this.logoFileId = brand?.logoFileId
    this.hasntBrandLogo = brand?.hasntBrandLogo
    this.backgroundColor = brand?.backgroundColor
    this.foregroundColor = brand?.foregroundColor
    this.accentColor = brand?.accentColor
    this.brandVoiceTones = brand?.brandVoiceTones?.length ? brand?.brandVoiceTones?.map(item => {
      return {
        label: item,
        value: item
      }
    }) : []
    this.brandPersonalities = brand?.brandPersonalities?.length ? brand?.brandPersonalities?.map(item => {
      return {
        label: item,
        value: item
      }
    }) : []
    this.brandTargetAudience = brand?.brandTargetAudience
    this.brandSocialMedias = brand?.brandSocialMedias ? brand?.brandSocialMedias : []

    this.isEditOrCreateBrand = true;

    this.currentStep = brand?.currentStep == 3 ? 2 : brand?.currentStep || 0

    let progress = 0

    this.steps.forEach((step, index) => {
      if (index < this.currentStep) {
        step.isCompleted = true;
        if (index == 0) {
          progress += 0.3
        }
        if (index == 1) {
          progress += 0.4
        }
        if (index == 2) {
          progress += 0.3
        }
      }
    })

    this.mainProgressBarValue = progress;

    this.initBrandOnboard()
  }

  // editBrand() {
  //   this.currentStep = 0
  // }

  initCreateBrand() {
    this.brandId = this.brandId || uuid4();

    document.getElementById('brand-guide-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    this.initBrandOnboard()
  }

  @watch('mainProgressBarValue')
  handleMainProgressBarValue() {
    if (this.createBrandProgressBarRef) {
      this.createBrandProgressBarRef.setProgress(this.mainProgressBarValue);
    }
  }

  initBrandOnboard() {
    if (this.brandId && !this.isBrandOnboardCompleted && this.currentStep < 3) {
      if (this.timeout) return

      this.timeout = setTimeout(() => {
        this.startTour();
      }, 5000)

    } else {
      return
    }
  }

  addSocialMedia(socialMedia: string) {

    const target = document.getElementById(socialMedia) as HTMLInputElement;

    const existingOption = this.brandSocialMedias?.find(item => item === socialMedia);

    if (!existingOption) {
      this.brandSocialMedias?.push(socialMedia);
      target.classList.add(...['!border-2', '!border-primary-500', 'dark:!border-tertiary-500', '!text-primary-500', 'dark:!text-tertiary-500'])

    } else {
      this.brandSocialMedias = this.brandSocialMedias?.filter(item => item !== socialMedia);
      target.classList.remove(...['!border-2', '!border-primary-500', 'dark:!border-tertiary-500', '!text-primary-500', 'dark:!text-tertiary-500'])
    }
  }

  getDisabledSaveBrand() {
    if (this.currentStep == (this.steps.length - 1)) {
      if (this.brandVoiceTones.length && this.brandPersonalities.length && this.brandSocialMedias.length && this.brandTargetAudience) {
        return false
      }
    }

    else {
      return true
    }
  }

  navigateStep(step: number) {
    if (!this.steps[step].isCompleted || this.currentStep == step) return

    this.currentStep = step;
    this.steps[step].progressBarRef.setProgress(1);
  }

  async searchBrandGuideSegmentCallback(query?: string): Promise<IFormInputOption[]> {
    return brandGuideSegment.filter(item => item.label?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().includes(query.toLowerCase())).slice(0, 20);
  }

  async searchBrandVoiceToneCallback(query?: string): Promise<IFormInputOption[]> {
    return voiceToneData.filter((i) => !this.brandVoiceTones.includes(i)).filter(item => item.label?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().includes(query.toLowerCase())).slice(0, 20);
  }

  async searchBrandPersonalityCallback(query?: string): Promise<IFormInputOption[]> {
    return personalityData.filter((i) => !this.brandPersonalities.includes(i)).filter(item => item.label?.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim().includes(query.toLowerCase())).slice(0, 20);
  }

  @watch('brandName')
  @watch('brandSegment')
  @watch('brandDescription')
  @watch('brandCharacteristics.length')
  handleFirstStepCharacteristics() {
    let progress = 0

    if (this.brandName) {
      progress += 0.3;
    }
    if (this.brandSegment) {
      progress += 0.3;
    }
    if (this.brandDescription) {
      progress += 0.3;
    }
    if (this.brandCharacteristics.length) {
      progress += 0.1;
    }

    if (this.steps[0].progressBarRef) {
      this.steps[0].progressBarRef.setProgress(progress);
    }
    this.steps[0].isCompleted = false;
    if (progress > 0.9) {
      if (Math.round(progress) == 1) {
        this.steps[0].isCompleted = true;
      }
    }

    this.setProgressBar()
  }

  @watch('accentColor')
  @watch('backgroundColor')
  @watch('foregroundColor')
  @watch('logoFile')
  @watch('hasntBrandLogo')
  handleSecondStepCharacteristics() {

    let progress = 0

    if (this.accentColor) {
      progress += 0.2;
    }
    if (this.backgroundColor) {
      progress += 0.2;
    }
    if (this.foregroundColor) {
      progress += 0.2;
    }
    if (this.logoFile || (this.hasntBrandLogo && !this.logoFile)) {
      progress += 0.4;
    }

    if (this.steps[1].progressBarRef) {
      this.steps[1].progressBarRef.setProgress(progress);
    }
    this.steps[1].isCompleted = false;
    if (progress > 0.9) {
      if (Math.round(progress) == 1) {
        this.steps[1].isCompleted = true;
      }
    }

    this.setProgressBar()
  }

  @watch('brandVoiceTones.length')
  @watch('brandPersonalities.length')
  @watch('brandTargetAudience')
  @watch('brandSocialMedias.length')
  @watch('postDescription')
  handleThirdStepCharacteristics() {
    let progress = 0

    if (this.brandVoiceTones.length) {
      progress += 0.25;
    }

    if (this.brandPersonalities.length) {
      progress += 0.25;
    }

    if (this.brandTargetAudience) {
      progress += 0.2;
    }

    if (this.brandSocialMedias.length) {
      progress += 0.2;
    }

    if (this.postDescription) {
      progress += 0.1;
    }

    if (this.steps[2].progressBarRef) {
      this.steps[2].progressBarRef.setProgress(progress);
    }
    this.steps[2].isCompleted = false;
    if (progress > 0.9) {
      if (Math.round(progress) == 1) {
        this.steps[2].isCompleted = true;
      }
    }

    this.setProgressBar()
  }

  async initBrandAnalysis() {

    const response = await this.zeusClient.chain('query')({
      generateBrandCharacteristics: [
        {
          params: {
            brandName: this.brandName,
            brandSegment: this.brandSegment.label,
            // brandSegment: this.brandSegmentValue,
            brandDescription: this.brandDescription
          }
        },
        {
          characteristics: true
        }
      ]
    })

    this.brandCharacteristics = response?.generateBrandCharacteristics?.characteristics;

  }

  async advanceToNextStep() {

    if (this.currentStep == 0) {
      const { valid } = await this.validationController.validate();

      if (!valid) return
    }

    this.setProgressBar()

    this.steps[this.currentStep].isCompleted = true;
    this.currentStep++

    document.getElementById('brand-guide-section')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    await this.saveBrandInfos();
  }

  setProgressBar() {
    let progress = 0;

    if (this.steps[0].isCompleted) {
      progress += 0.3
    }

    if (this.steps[1].isCompleted) {
      progress += 0.4
    }

    if (this.steps[2].isCompleted) {
      progress += 0.3
    }

    this.mainProgressBarValue = progress;
  }

  async saveBrandInfos() {

    let logoResponse: StorageUploadFileResponse | null = null;

    if (this.logoFile && !this.hasntBrandLogo) {
      logoResponse = await nhost.storage.upload({
        bucketId: 'public',
        file: this.logoFile,
        ...this.logoFileId && { fileId: this.logoFileId }
      });

      this.logoFileId = logoResponse.fileMetadata.id;
    }

    this.brandId = this.brandId || uuid4();

    const response = await this.zeusClient.chain('mutation')({
      insert_brands_one: [
        {
          object: {
            id: this.brandId,
            brandName: this.brandName,
            segment: this.brandSegment.label,
            // segment: this.brandSegmentValue,
            brandDescription: this.brandDescription,
            brandCharacteristics: this.brandCharacteristics,
            logoFileId: this.logoFileId,
            hasntBrandLogo: this.hasntBrandLogo ? true : false,
            backgroundColor: this.backgroundColor,
            foregroundColor: this.foregroundColor,
            accentColor: this.accentColor,
            brandVoiceTones: this.brandVoiceTones.map(item => item.value) as string[],
            brandPersonalities: this.brandPersonalities.map(item => item.value) as string[],
            brandTargetAudience: this.brandTargetAudience,
            brandSocialMedias: this.brandSocialMedias,
          },
          on_conflict: {
            constraint: brands_constraint.brands_pkey,
            update_columns: [
              brands_update_column.brandName,
              brands_update_column.segment,
              brands_update_column.brandDescription,
              brands_update_column.brandCharacteristics,
              brands_update_column.logoFileId,
              brands_update_column.hasntBrandLogo,
              brands_update_column.backgroundColor,
              brands_update_column.foregroundColor,
              brands_update_column.accentColor,
              brands_update_column.brandVoiceTones,
              brands_update_column.brandPersonalities,
              brands_update_column.brandTargetAudience,
              brands_update_column.brandSocialMedias
            ],
          }
        },
        {
          id: true,
        }
      ]
    })

    return response?.insert_brands_one
  }

  async finishBrand() {

    this.finishBrandLoading = true;

    const response = await this.saveBrandInfos()

    if (!response) throw new Error('Erro ao finalizar marca.');

    this.finishBrandLoading = false;

    this.currentStep = 3;

  }

  async testStyleText() {
    const body = {
      brandName: this.brandName,
      brandCharacteristics: this.brandCharacteristics,
      brandTargetAudience: this.brandTargetAudience,
      brandSocialMedias: this.brandSocialMedias,
      brandVoiceTones: this.brandVoiceTones.map(item => item.value) as string[],
      brandPersonalities: this.brandPersonalities.map(item => item.value) as string[],
    }

    const response = await this.zeusClient.chain('query')({
      testBrandStyleText: [
        {
          params: {
            ...body
          }
        },
        {
          postDescription: true
        }
      ]
    })

    if (!response?.testBrandStyleText) throw new Error('Erro ao testar estilo de texto.');

    this.postDescription = response.testBrandStyleText.postDescription;

    if (window.innerWidth < 768) this.openPostExampleDrawer()

  }

  async restartBrand() {
    const { dialog } = await this.dialogService.open({
      component: () => RestartBrandComponent,
      startingZIndex: 100,
    })

    const result = await dialog.closed;

    if (result.status === 'ok') {
      this.brandDescription = null
      this.brandCharacteristics = []
    }

  }

  initAddMoreCharacteristic() {
    this.showInputAddCharacteristic = true;
  }

  closeAddCharacteristic() {
    this.showInputAddCharacteristic = false;
  }

  addCharacteristic() {
    this.brandCharacteristics.push(this.newCharacteristic);
    this.newCharacteristic = '';

    this.showInputAddCharacteristic = false;
  }

  removeCharacteristic(index: number) {
    this.brandCharacteristics.splice(index, 1);
  }

  removeVoiceTone(index: number) {
    this.brandVoiceTones.splice(index, 1);
  }

  removePersonality(index: number) {
    this.brandPersonalities.splice(index, 1);
  }

  @watch('brandVoiceTone')
  addBrandVoiceToneOption() {

    if (!this.brandVoiceTone) return

    const existingOption = this.brandVoiceTones.find(item => item.value === this.brandVoiceTone.value);

    if (!existingOption && this.brandVoiceTones?.length < 2) {
      this.brandVoiceTones.push(this.brandVoiceTone);

      this.voiceToneData = voiceToneData.filter((i) => !this.brandVoiceTones.includes(i))

      this.brandVoiceTone = null;
      this.brandVoiceToneValue = null;
    }
  }

  @watch('brandPersonality')
  addBrandPersonalityOption() {

    if (!this.brandPersonality) return

    const existingOption = this.brandPersonalities.find(item => item.value === this.brandPersonality.value);

    if (!existingOption && this.brandPersonalities?.length < 2) {
      this.brandPersonalities.push(this.brandPersonality);

      this.personalityData = personalityData.filter((i) => !this.brandPersonalities.includes(i))

      this.brandPersonality = null;
      this.brandPersonalityValue = null;
    }
  }

  async openPostExampleDrawer() {
    const { dialog } = await this.dialogService.open({
      component: () => PostExampleDrawerComponent,
      model: {
        brandName: this.brandName,
        brandSegment: this.brandSegment,
        backgroundColor: this.backgroundColor,
        accentColor: this.accentColor,
        foregroundColor: this.foregroundColor,
        postDescription: this.postDescription,
        currentFile: this.currentFile,
        logoFileId: this.logoFileId,
        currentStep: this.currentStep,
        steps: this.steps,
        mainProgressBarValue: this.mainProgressBarValue,
        saveBrandFunction: () => this.finishBrand(),
      },
      startingZIndex: 100,

    })
  }

  private getShade(color: string, index: string | number): string {
    const shades = chroma.scale([color, '#ffffff']).colors(7)
    return shades[index];
  }

  private loadFont(fontPrimary: string) {
    WebFont.load({
      google: {
        families: [fontPrimary]
      },
      fontactive: (familyName: string) => {
        this.applyFont(familyName);
      }
    });
  }

  private applyFont(familyName: string) {
    if (this.titleRef) {
      this.titleRef.style.fontFamily = familyName;
    }

    if (this.headlineRef) {
      this.headlineRef.style.fontFamily = familyName;
    }
  }

  private logoFileChanged(file: File) {
    const fileReader = new FileReader();

    fileReader.onloadend = () => {
      const base64Data = fileReader.result as string;

      const fileId = Math.random().toString(36).substring(2, 9);

      const fileData: CurrentFile = {
        id: fileId,
        file: base64Data,
        size: file.size,
      }

      this.currentFile = fileData;
    }

    if (file) {
      fileReader.readAsDataURL(file);
    }

  }
}

export type CurrentFile = {
  id: string;
  file: string;
  size: number;
}